import { useState } from 'react';
import { useLocation } from '@/context/LocationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapPin, Navigation, Loader2, Check, X, Settings } from 'lucide-react';

export default function LocationSelector({ 
  variant = 'badge', // 'badge', 'button', 'inline', 'compact'
  showDetectButton = true,
  onLocationChange = null,
  className = ''
}) {
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(location.region || '');
  const [selectedParish, setSelectedParish] = useState(location.parish || '');

  const handleDetect = () => {
    location.detectLocation();
  };

  const handleSave = () => {
    if (selectedParish) {
      location.setManualLocation(selectedParish, selectedRegion);
      if (onLocationChange) {
        onLocationChange({ parish: selectedParish, region: selectedRegion });
      }
      setShowModal(false);
    }
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    setSelectedParish(''); // Reset parish when region changes
  };

  const availableParishes = selectedRegion 
    ? location.getParishesByRegion(selectedRegion)
    : location.getParishes();

  // Badge variant - shows location with click to change
  if (variant === 'badge') {
    return (
      <>
        <Badge 
          className={`cursor-pointer hover:bg-[#006847] bg-[#006847]/80 text-white ${className}`}
          onClick={() => setShowModal(true)}
        >
          <MapPin className="h-3 w-3 mr-1" />
          {location.loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : location.parish ? (
            <span>{location.parish} Parish</span>
          ) : (
            <span>Set Location</span>
          )}
        </Badge>
        <LocationModal 
          open={showModal} 
          onClose={() => setShowModal(false)}
          location={location}
          selectedRegion={selectedRegion}
          selectedParish={selectedParish}
          onRegionChange={handleRegionChange}
          onParishChange={setSelectedParish}
          onSave={handleSave}
          onDetect={handleDetect}
          availableParishes={availableParishes}
          showDetectButton={showDetectButton}
        />
      </>
    );
  }

  // Button variant - larger button for prominent placement
  if (variant === 'button') {
    return (
      <>
        <Button 
          variant="outline" 
          onClick={() => setShowModal(true)}
          className={className}
        >
          <MapPin className="h-4 w-4 mr-2" />
          {location.loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : location.parish ? (
            <span>{location.parish} Parish</span>
          ) : (
            <span>Set Your Location</span>
          )}
        </Button>
        <LocationModal 
          open={showModal} 
          onClose={() => setShowModal(false)}
          location={location}
          selectedRegion={selectedRegion}
          selectedParish={selectedParish}
          onRegionChange={handleRegionChange}
          onParishChange={setSelectedParish}
          onSave={handleSave}
          onDetect={handleDetect}
          availableParishes={availableParishes}
          showDetectButton={showDetectButton}
        />
      </>
    );
  }

  // Compact variant - just an icon button
  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${className}`}
          title={location.parish ? `${location.parish} Parish` : 'Set Location'}
        >
          {location.loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-[#006847]" />
          ) : (
            <MapPin className={`h-5 w-5 ${location.parish ? 'text-[#006847]' : 'text-gray-400'}`} />
          )}
        </button>
        <LocationModal 
          open={showModal} 
          onClose={() => setShowModal(false)}
          location={location}
          selectedRegion={selectedRegion}
          selectedParish={selectedParish}
          onRegionChange={handleRegionChange}
          onParishChange={setSelectedParish}
          onSave={handleSave}
          onDetect={handleDetect}
          availableParishes={availableParishes}
          showDetectButton={showDetectButton}
        />
      </>
    );
  }

  // Inline variant - for embedding in forms
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Your Location</label>
        {showDetectButton && (
          <Button 
            type="button"
            variant="ghost" 
            size="sm" 
            onClick={handleDetect}
            disabled={location.loading}
          >
            {location.loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Navigation className="h-4 w-4 mr-1" />
            )}
            Detect
          </Button>
        )}
      </div>
      
      {location.parish && (
        <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <span className="text-sm text-green-800">
            {location.parish} Parish ({location.source === 'gps' ? 'Auto-detected' : 'Saved'})
          </span>
          <Button 
            type="button"
            variant="ghost" 
            size="sm" 
            className="ml-auto p-1 h-6"
            onClick={() => setShowModal(true)}
          >
            <Settings className="h-3 w-3" />
          </Button>
        </div>
      )}

      {!location.parish && (
        <div className="flex gap-2">
          <Select value={selectedRegion} onValueChange={handleRegionChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent>
              {location.getRegions().map((region) => (
                <SelectItem key={region} value={region}>
                  {region.charAt(0).toUpperCase() + region.slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={selectedParish} 
            onValueChange={(parish) => {
              setSelectedParish(parish);
              location.setManualLocation(parish, selectedRegion);
              if (onLocationChange) {
                onLocationChange({ parish, region: selectedRegion });
              }
            }}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select parish" />
            </SelectTrigger>
            <SelectContent>
              {availableParishes.map((parish) => (
                <SelectItem key={parish} value={parish}>
                  {parish}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {location.permissionDenied && (
        <p className="text-xs text-amber-600">
          Location access denied. Please select manually or enable location in browser settings.
        </p>
      )}

      <LocationModal 
        open={showModal} 
        onClose={() => setShowModal(false)}
        location={location}
        selectedRegion={selectedRegion}
        selectedParish={selectedParish}
        onRegionChange={handleRegionChange}
        onParishChange={setSelectedParish}
        onSave={handleSave}
        onDetect={handleDetect}
        availableParishes={availableParishes}
        showDetectButton={showDetectButton}
      />
    </div>
  );
}

// Location Modal Component
function LocationModal({
  open,
  onClose,
  location,
  selectedRegion,
  selectedParish,
  onRegionChange,
  onParishChange,
  onSave,
  onDetect,
  availableParishes,
  showDetectButton
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#006847]" />
            Set Your Location
          </DialogTitle>
          <DialogDescription>
            Your location helps us show you local events, businesses, weather alerts, and more.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Location */}
          {location.parish && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-800">{location.parish} Parish</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {location.source === 'gps' ? 'Auto-detected' : location.source === 'manual' ? 'Manual' : 'Saved'}
                </Badge>
              </div>
              {location.region && (
                <p className="text-sm text-green-600 mt-1">Region: {location.region}</p>
              )}
            </div>
          )}

          {/* Auto-detect Button */}
          {showDetectButton && (
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={onDetect}
              disabled={location.loading}
            >
              {location.loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Navigation className="h-4 w-4 mr-2" />
              )}
              {location.loading ? 'Detecting...' : 'Auto-Detect My Location'}
            </Button>
          )}

          {location.permissionDenied && (
            <p className="text-sm text-amber-600 text-center">
              Location access was denied. Please select manually below.
            </p>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-500">or select manually</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Manual Selection */}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Region</label>
              <Select value={selectedRegion} onValueChange={onRegionChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your region" />
                </SelectTrigger>
                <SelectContent>
                  {location.getRegions().map((region) => (
                    <SelectItem key={region} value={region}>
                      {region.charAt(0).toUpperCase() + region.slice(1).replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Parish</label>
              <Select value={selectedParish} onValueChange={onParishChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your parish" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {availableParishes.map((parish) => (
                    <SelectItem key={parish} value={parish}>
                      {parish}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {location.parish && (
              <Button variant="ghost" onClick={location.clearLocation} className="text-red-600">
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
            <Button 
              className="flex-1 bg-[#006847] hover:bg-[#005538]"
              onClick={onSave}
              disabled={!selectedParish}
            >
              <Check className="h-4 w-4 mr-2" />
              Save Location
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
